import { FiImageSize, FileType } from '@fi-shared/types';
import { Injectable } from '@angular/core';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { MediaBankStorageType } from '@fi-shared/media-bank/media-bank-storage-type';
import { FiVimeoService, VIMEO_PLAYER_URL } from '@fi-shared/media-bank/fi-vimeo/fi-vimeo.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { AppTextService } from '@fi-shared/app-text';
import { FiMediaBankDownloadDialogComponent } from '@fi-shared/media-bank/fi-media-bank-download-dialog/fi-media-bank-download-dialog.component';
import { FiMediaBankDownloadData } from '@fi-shared/media-bank/media-bank';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { getFileParts } from '@fi-shared/utils/get-file-parts';
import { FiAppSettings } from '@fi-shared/fi-app-settings';
import { safeEndsWith, safeStartsWith } from '@fi-shared/utils/safe-starts-with';
import { isDefined } from '@fi-shared/utils';

type TContentDisposition = 'inline' | 'attachment';

export interface IMediaFileUrlArgs {
    openPdfInNewWindow?: boolean;
    allowMultipleDownloads?: boolean;
}

@Injectable()
export class FiMediaStorageService {
    constructor(
        protected fiCustomerDataService: FiCustomerDataService,
        private fiBackend: FiBackendService,
        private vimeoService: FiVimeoService,
        private fiDialogsService: FiDialogsService,
        private loadingService: LoadingService,
        private appTextService: AppTextService,
    ) {}

    private buildStorageUrl(storageDomain: string, customerName: string, path: string) {
        return `${storageDomain}/${customerName.toLowerCase()}/${path}`;
    }

    getFullStorageUrl(path: string): string {
        return this.buildStorageUrl(
            this.fiCustomerDataService.getCustomerData().StorageDomain,
            this.fiCustomerDataService.getCustomerData().CustomerName,
            path,
        );
    }

    private isOriginalSize(size: FiImageSize): boolean {
        return size == undefined || size === FiImageSize.Original;
    }

    getMediaFileUrl(
        model: {
            Url?: string;
            PhysicalFileName?: string;
            Extension?: string;
            ChangedOn?: string;
            StorageType?: MediaBankStorageType;
            FullName?: string; // ccm-text-item-media-model in some old documents
        },
        args?: {
            size?: FiImageSize;
            contentDisposition?: TContentDisposition;
            alwaysUseDownloadFromObjectStorageUrl?: boolean;
        },
    ): string {
        if (!isDefined(args)) {
            args = {};
        }
        if (!isDefined(args.size)) {
            args.size = FiImageSize.Original;
        }
        if (!isDefined(args.contentDisposition)) {
            args.contentDisposition = 'inline';
        }
        const size = args.size;

        if (model.StorageType === MediaBankStorageType.Vimeo) {
            return `${VIMEO_PLAYER_URL}/${model.PhysicalFileName}`;
        }

        if (
            (model.Extension === FiAppSettings.DefaultMediaBankPdfExtension && this.isOriginalSize(size)) ||
            args.alwaysUseDownloadFromObjectStorageUrl
        ) {
            // special case for pdf
            const url = this.getDownloadFromObjectStorageFullUrl(
                model.PhysicalFileName,
                model.Extension,
                size,
                args.contentDisposition,
            );
            return url;
        }

        if (model.Url && (!model.PhysicalFileName || !model.Extension)) {
            // https://chainformation.atlassian.net/browse/FI-3807
            // additional check, storage domain should be same as in Url
            const storageDomain = this.fiCustomerDataService.getCustomerData().StorageDomain;
            if (model.Url.indexOf(storageDomain) !== -1) {
                return model.Url;
            }
        }

        if (!model.PhysicalFileName || !model.Extension) {
            if (model.FullName) {
                return this.getMediaFileUrlByFileName(model.FullName, size);
            }
            return null;
        }

        /*if (this.shouldIgnoreSize(model.Extension)) {
            size = undefined;
        }*/
        const ext =
            size && !this.shouldPreserveExtension(model.Extension)
                ? FiAppSettings.DefaultMediaBankImageExtension
                : model.Extension;
        const fileName = model.PhysicalFileName + ext;

        return this.getMediaFileUrlByFileName(fileName, size, model.ChangedOn);
    }

    // fileName - PhysicalFileName, usually guid
    getMediaFileUrlByFileName(fileName: string, size: FiImageSize, changedOn?: string) {
        const fileParts = fileName.split('.');
        let sizeUrlPart: string; // size part in file url on blob storage

        // we should show gifs in original size
        // https://chainformation.atlassian.net/browse/FI-3860
        if (this.isOriginalSize(size)) {
            sizeUrlPart = FiImageSize[FiImageSize.Original] + '/';
        } else {
            // it is image, change extension to "jpg"
            if (fileParts.length > 1 && !this.shouldPreserveExtension('.' + fileParts[fileParts.length - 1])) {
                fileParts[fileParts.length - 1] = 'jpg';
                fileName = fileParts.join('.');
            }
            sizeUrlPart = this.getSizeUrlPart(size);
        }

        let url =
            this.fiCustomerDataService.getCustomerData().StorageDomain +
            '/' +
            this.fiCustomerDataService.getCustomerData().CustomerName.toLowerCase() +
            '/' +
            sizeUrlPart +
            encodeURIComponent(fileName);
        if (changedOn) {
            url += '?time=' + changedOn;
        }

        return url;
    }

    private getDownloadFromObjectStorageFullUrl(
        physicalFileName: string,
        extension: string,
        imageSize: FiImageSize = FiImageSize.Medium,
        contentDisposition?: TContentDisposition,
    ): string {
        const path = this.getDownloadFromObjectStorageUrl(physicalFileName, extension, imageSize, contentDisposition);
        return this.fiBackend.getRequestUrl(path);
    }

    private getDownloadFromObjectStorageUrl(
        physicalFileName: string,
        extension: string,
        imageSize: FiImageSize,
        contentDisposition: TContentDisposition,
    ): string {
        let url = `MediaBank/FileUpload/DownloadFromObjectStorage?physicalFileName=${physicalFileName}&extension=${extension}&size=${imageSize}`;
        if (isDefined(contentDisposition)) {
            url += `&contentDisposition=${contentDisposition}`;
        }

        return url;
    }

    getDefaultVimeoPreview() {
        const url = this.fiBackend.getRequestUrl('MediaBank/MediaBank/GetDefaultVimeoPreview');
        return url;
    }

    isVimeoStorage(storageType: MediaBankStorageType) {
        return storageType === MediaBankStorageType.Vimeo;
    }

    canShowInIframe(fileType: FileType, extension: string): boolean {
        return fileType === FileType.Pdf || fileType === FileType.PdfNoPreview; // || extension?.toLowerCase() === '.txt';
    }

    getMediaFileUrlForDownload(model: {
        Id?: number;
        MediaBankFileId?: number;
        PhysicalFileName?: string;
        Extension?: string;
        StorageType?: MediaBankStorageType;
        FullName?: string;
        FileType?: FileType;
    }) {
        return new Promise<FiMediaBankDownloadData>((resolve, reject) => {
            const checkDownloadDataFn = (downloadData: FiMediaBankDownloadData[]) => {
                if (downloadData.length === 1) {
                    resolve(downloadData[0]);
                } else {
                    this.openMediaBankDownloadDialog(downloadData).then(
                        (selectedDownloadItem: FiMediaBankDownloadData) => resolve(selectedDownloadItem),
                        () => resolve(null),
                    );
                }
            };

            if (model.StorageType === MediaBankStorageType.Vimeo) {
                this.vimeoService.getDownloadInfo(model.PhysicalFileName).then((downloadData) => {
                    checkDownloadDataFn(downloadData);
                }, reject);
            } else {
                let physicalFileName = model.PhysicalFileName;
                let extension = model.Extension;
                if (!physicalFileName) {
                    const fileParts = getFileParts(model.FullName);
                    physicalFileName = fileParts.fileName;
                    extension = fileParts.extension;
                }

                this.getMediaDownloadData(physicalFileName, extension).then((downloadData) => {
                    checkDownloadDataFn(
                        downloadData.filter(
                            (item) => model.FileType === FileType.Image || item.imageSize === FiImageSize.Original,
                        ),
                    );
                }, reject);
            }
        });
    }

    private getMediaDownloadData(physicalFileName: string, extension: string) {
        const url = `MediaBank/FileUpload/GetMediaDownloadData?fileName=${encodeURIComponent(physicalFileName)}&extension=${encodeURIComponent(extension)}`;
        return this.loadingService.trackPromise(
            this.fiBackend.fetchData<FiMediaBankDownloadData[]>(url, { supressErrorMessage: true }),
        );
    }

    private openMediaBankDownloadDialog(downloadData: FiMediaBankDownloadData[]): Promise<FiMediaBankDownloadData> {
        return new Promise<FiMediaBankDownloadData>((resolve, reject) => {
            this.fiDialogsService
                .custom({
                    headerText: this.appTextService.get('common.download'),
                    contentComponentRef: FiMediaBankDownloadDialogComponent,
                    contentComponentData: downloadData,
                })
                .onYesButtonClick((selectedDownloadItem) => resolve(selectedDownloadItem))
                .onNoButtonClick(() => reject());
        });
    }

    // https://chainformation.atlassian.net/browse/FI-3860
    shouldPreserveExtension(extension: string) {
        return ['.gif'].indexOf((extension || '').toLowerCase()) !== -1;
    }

    // https://chainformation.atlassian.net/browse/FI-4277
    // image size changes
    private imageSizeV2Map: { [prop: number]: string };
    private getSizeUrlPart(size: FiImageSize): string {
        if (!this.imageSizeV2Map) {
            this.imageSizeV2Map = {};
            this.imageSizeV2Map[FiImageSize.Thumbnail] = FiImageSize[FiImageSize.Thumbnail];
            this.imageSizeV2Map[FiImageSize.Large] = FiImageSize[FiImageSize.Large];
            this.imageSizeV2Map[FiImageSize.Medium] = FiImageSize[FiImageSize.Medium];
            this.imageSizeV2Map[FiImageSize.Small] = FiImageSize[FiImageSize.Small];
            this.imageSizeV2Map[FiImageSize.AttachmentPreview] = FiImageSize[FiImageSize.Logo];
            this.imageSizeV2Map[FiImageSize.Logo] = FiImageSize[FiImageSize.Logo];
            this.imageSizeV2Map[FiImageSize.Background] = FiImageSize[FiImageSize.Background];
            this.imageSizeV2Map[FiImageSize.Original] = FiImageSize[FiImageSize.Original];
        }

        const sizeUrlPart = this.imageSizeV2Map[size];

        if (sizeUrlPart) return sizeUrlPart + '/';

        return <any>size + '/';
    }

    isImageWithMetadata(file: File) {
        return safeStartsWith(file.type, 'image/') || safeEndsWith(file.name, '.heic');
    }

    isVideoWithMetadata(file: File) {
        return safeStartsWith(file.type, 'video/');
    }

    isFileWithMetadata(file: File) {
        // https://chainformation.atlassian.net/browse/FI-5816
        // heic files have empty 'type' property
        return this.isImageWithMetadata(file) || this.isVideoWithMetadata(file);
    }
}
